/**
 * FmCashflowChart — income/cost bars with a net line, or signed net bars.
 *
 * A period is picked by clicking its bars or, on the keyboard, by moving the
 * active period with the arrow keys and pressing Enter. With a period
 * selected only the bars outside it dim.
 */
import React from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { FmCashflowChartSpec, FmCashflowPoint, FmChartSize } from './chartTypes';
import {
  CHART_DIMMED_OPACITY,
  CHART_GRID_PROPS,
  chartMargin,
  CHART_XAXIS_PROPS,
  CHART_YAXIS_PROPS,
  xAxisTitle,
  yAxisTitle,
} from './FmChartChrome.constants';
import { FmChartTooltip } from './FmChartChrome';
import { barCellShape } from './barCellShape';
import { FmChartFrame } from './FmChartFrame';
import { categoryAxisLayout, categoryAxisProps } from './categoryAxis';
import { useChartCategorySelection } from './useChartCategorySelection';
import { CASHFLOW_COLORS } from './chartRoleColors';

export type FmCashflowChartProps = Omit<FmCashflowChartSpec, 'kind'> & {
  size?: FmChartSize;
  'data-testid'?: string;
};

const COLORS = CASHFLOW_COLORS;

/** A period tick shows its first year; the tooltip carries the full span. */
function periodStart(period: string): string {
  return period.split(/[–-]/u)[0] ?? period;
}

function signedNetColor(net: number | null | undefined): string {
  if (net === null || net === undefined || net === 0) return COLORS.net;
  return net < 0 ? COLORS.netNeg : COLORS.netPos;
}

function periodOpacity(period: string, activePeriod: string | undefined): number {
  return activePeriod === undefined || activePeriod === period ? 1 : CHART_DIMMED_OPACITY;
}

function periodShape(activePeriod: string | undefined, fill: (point: FmCashflowPoint) => string) {
  return barCellShape<FmCashflowPoint>((point) => ({
    fill: fill(point),
    fillOpacity: periodOpacity(point.period, activePeriod),
  }));
}

export const FmCashflowChart: React.FC<FmCashflowChartProps> = ({
  data,
  render = 'grouped-bars-net-line',
  xLabel,
  yLabel,
  seriesLabels,
  activePeriod,
  onPickPeriod,
  formatValue,
  size = 'md',
  'data-testid': testId,
}) => {
  const selection = useChartCategorySelection(onPickPeriod, 'period');
  const rows = data.map((point) => ({ ...point }));
  const [frameWidth, setFrameWidth] = React.useState<number | null>(null);
  const axis = categoryAxisLayout(
    rows.map((point) => periodStart(point.period)),
    frameWidth,
  );
  return (
    <FmChartFrame
      size={size}
      onKeyDown={selection.onKeyDown}
      onWidth={setFrameWidth}
      data-testid={testId}
      data-kind="cashflow"
    >
      <ComposedChart
        data={rows}
        margin={chartMargin(yLabel)}
        accessibilityLayer
        onClick={selection.onClick}
      >
        <CartesianGrid {...CHART_GRID_PROPS} />
        <XAxis
          dataKey="period"
          {...CHART_XAXIS_PROPS}
          {...categoryAxisProps(axis, periodStart)}
          label={rows.length > 1 ? xAxisTitle(xLabel) : undefined}
        />
        <YAxis {...CHART_YAXIS_PROPS} label={yAxisTitle(yLabel)} tickFormatter={formatValue} />
        <Tooltip
          content={<FmChartTooltip formatValue={formatValue} />}
          cursor={{ fill: 'var(--fm-d-surface-3)' }}
        />
        <ReferenceLine y={0} stroke="var(--fm-chart-axis)" />
        {render === 'grouped-bars-net-line' ? (
          <>
            <Bar
              dataKey="income"
              name={seriesLabels.income}
              fill={COLORS.income}
              isAnimationActive={false}
              onClick={selection.onBarClick}
              shape={periodShape(activePeriod, () => COLORS.income)}
            />
            <Bar
              dataKey="cost"
              name={seriesLabels.cost}
              fill={COLORS.cost}
              isAnimationActive={false}
              onClick={selection.onBarClick}
              shape={periodShape(activePeriod, () => COLORS.cost)}
            />
            <Line
              type="linear"
              dataKey="net"
              name={seriesLabels.net}
              stroke={COLORS.net}
              strokeWidth={2}
              dot={{ r: 3, fill: COLORS.net, strokeWidth: 0 }}
              connectNulls={false}
              isAnimationActive={false}
            />
          </>
        ) : (
          <Bar
            dataKey="net"
            name={seriesLabels.net}
            minPointSize={2}
            isAnimationActive={false}
            onClick={selection.onBarClick}
            shape={periodShape(activePeriod, (point) => signedNetColor(point.net))}
          />
        )}
        {selection.reporter}
      </ComposedChart>
    </FmChartFrame>
  );
};
